// src/features/merchantConfig/components/ValueBlockRenderer.jsx
//
// The recursive heart of the screen. Given an arbitrary JSON value block —
// no compile-time schema — produce a structured, scannable rendering per UI
// prompt §5.
//
// Dispatch table (mirrors §5):
//   * scalar / null              -> FieldRow leaf
//   * inline array of scalars    -> FieldRow leaf (comma-separated + "+N more")
//   * array of objects           -> ArrayTable
//   * mixed / nested array       -> recurse on each element with index in path
//   * object (non-empty)         -> ObjectSection (collapsible) + recurse
//   * object (empty)             -> ObjectSection in "no data" mode
//
// Phase-2 readiness:
//   * `readOnly` and `onEdit` are threaded through every level.
//   * Every leaf's `path` is the full JSON path from the value-block root —
//     ready for `buildPatchBody(path, newValue)` in Phase 2.

import React from "react";
import FieldRow from "./FieldRow";
import ObjectSection from "./ObjectSection";
import ArrayTable from "./ArrayTable";
import { humanizeLabel } from "../lib/humanizeLabel";
import { pathToKey } from "../lib/jsonPath";

/* ---------- Type guards ---------- */

const isPlainObject = (v) =>
  v !== null && typeof v === "object" && !Array.isArray(v);

const isScalarLike = (v) =>
  v === null ||
  v === undefined ||
  typeof v === "string" ||
  typeof v === "number" ||
  typeof v === "boolean";

const isArrayOfScalars = (arr) =>
  Array.isArray(arr) && arr.every(isScalarLike);

const isArrayOfObjects = (arr) =>
  Array.isArray(arr) && arr.length > 0 && arr.every(isPlainObject);

/* ---------- Helpers for array-of-scalars inline rendering ---------- */

const SCALAR_ARRAY_TRUNCATE_AT = 5;

function previewScalarArray(arr) {
  if (arr.length === 0) return "[]";
  const head = arr.slice(0, SCALAR_ARRAY_TRUNCATE_AT).map(formatScalar);
  if (arr.length <= SCALAR_ARRAY_TRUNCATE_AT) return head.join(", ");
  return `${head.join(", ")}, +${arr.length - SCALAR_ARRAY_TRUNCATE_AT} more`;
}

function formatScalar(v) {
  if (v === null || v === undefined) return "—";
  if (typeof v === "boolean") return v ? "Enabled" : "Disabled";
  if (typeof v === "number") return v.toLocaleString();
  return String(v);
}

/* ---------- Main recursive renderer ---------- */

/**
 * @param {{
 *   value: unknown,
 *   fieldKey?: string,
 *   path?: Array<string | number>,
 *   depth?: number,
 *   readOnly?: boolean,
 *   onEdit?: (path: Array<string | number>, newValue: unknown) => void,
 *   topLevelLabel?: string,
 *   configKey?: string,
 * }} props
 */
export default function ValueBlockRenderer({
  value,
  fieldKey,
  path = [],
  depth = 0,
  readOnly = true,
  onEdit,
  topLevelLabel,
  configKey,
}) {
  // --- ARRAY branches ---
  if (Array.isArray(value)) {
    // Empty array: friendly placeholder via FieldRow-like row.
    if (value.length === 0) {
      return (
        <div
          key={pathToKey(path)}
          className="rounded-md px-2 py-2 text-sm text-gray-500"
        >
          <span className="text-xs font-medium uppercase tracking-wide text-gray-500">
            {humanizeLabel(fieldKey || "")}
          </span>
          <span className="ml-3 text-gray-400">— no items</span>
        </div>
      );
    }

    // All scalars: array-of-scalars. In edit mode the FieldRow will swap to
    // ArrayOfScalarsEditor (which understands arrays); in view mode we render
    // the comma-separated preview text.
    if (isArrayOfScalars(value)) {
      return (
        <FieldRow
          key={pathToKey(path)}
          fieldKey={fieldKey || ""}
          value={readOnly ? previewScalarArray(value) : value}
          path={path}
          readOnly={readOnly}
          onEdit={onEdit}
          configKey={configKey}
        />
      );
    }

    // All objects: render as sortable table.
    if (isArrayOfObjects(value)) {
      return (
        <ArrayTable
          key={pathToKey(path)}
          fieldKey={fieldKey || ""}
          items={value}
          path={path}
          readOnly={readOnly}
          onEdit={onEdit}
          configKey={configKey}
        />
      );
    }

    // Mixed / nested array: recurse per element. This is rare in practice but
    // we never want to crash on unexpected data.
    return (
      <ObjectSection
        key={pathToKey(path)}
        fieldKey={fieldKey}
        depth={depth + 1}
        childCount={value.length}
      >
        <div className="space-y-1">
          {value.map((item, i) => (
            <ValueBlockRenderer
              key={pathToKey([...path, i])}
              value={item}
              fieldKey={String(i)}
              path={[...path, i]}
              depth={depth + 2}
              readOnly={readOnly}
              onEdit={onEdit}
              configKey={configKey}
            />
          ))}
        </div>
      </ObjectSection>
    );
  }

  // --- OBJECT branches ---
  if (isPlainObject(value)) {
    const keys = Object.keys(value);
    const isEmpty = keys.length === 0;

    // Partition into scalars-or-arrays-of-scalars (rendered first as a flat
    // dl-style group) and nested objects/arrays (rendered after).
    const flatKeys = [];
    const nestedKeys = [];
    for (const k of keys) {
      const child = value[k];
      if (isPlainObject(child) || (Array.isArray(child) && !isArrayOfScalars(child))) {
        nestedKeys.push(k);
      } else {
        flatKeys.push(k);
      }
    }

    return (
      <ObjectSection
        key={pathToKey(path)}
        fieldKey={fieldKey}
        labelOverride={
          depth === 0 ? topLevelLabel ?? (fieldKey ? humanizeLabel(fieldKey) : undefined) : undefined
        }
        depth={depth}
        childCount={keys.length}
        isEmpty={isEmpty}
      >
        {!isEmpty && (
          <>
            {flatKeys.length > 0 && (
              <dl
                className={
                  depth === 0
                    ? "grid grid-cols-1 gap-y-1 sm:grid-cols-2 sm:gap-x-6"
                    : "space-y-0.5"
                }
              >
                {flatKeys.map((k) => (
                  <ValueBlockRenderer
                    key={pathToKey([...path, k])}
                    value={value[k]}
                    fieldKey={k}
                    path={[...path, k]}
                    depth={depth + 1}
                    readOnly={readOnly}
                    onEdit={onEdit}
                    configKey={configKey}
                  />
                ))}
              </dl>
            )}

            {nestedKeys.length > 0 && (
              <div className={`space-y-3 ${flatKeys.length > 0 ? "mt-3" : ""}`}>
                {nestedKeys.map((k) => (
                  <ValueBlockRenderer
                    key={pathToKey([...path, k])}
                    value={value[k]}
                    fieldKey={k}
                    path={[...path, k]}
                    depth={depth + 1}
                    readOnly={readOnly}
                    onEdit={onEdit}
                    configKey={configKey}
                  />
                ))}
              </div>
            )}
          </>
        )}
      </ObjectSection>
    );
  }

  // --- LEAF (scalar / null) ---
  return (
    <FieldRow
      key={pathToKey(path)}
      fieldKey={fieldKey || ""}
      value={value}
      path={path}
      readOnly={readOnly}
      onEdit={onEdit}
      configKey={configKey}
    />
  );
}
